/**
 * Pure TEA update for EditorShell chrome that is not session-owned.
 *
 * Composes NewProjectDialog and the About launcher so the shell keeps one
 * `useTea` loop.
 * Shell: `EditorShell.vue`.
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";
import {
  initialNewProjectDialogModel,
  updateNewProjectDialog,
  type NewProjectDialogCmd,
  type NewProjectDialogModel,
  type NewProjectDialogMsg,
} from "./NewProjectDialog.update";
import {
  initialAboutLauncherModel,
  updateAboutLauncher,
  type AboutLauncherCmd,
  type AboutLauncherModel,
  type AboutLauncherMsg,
} from "./AboutPanel.update";

export type EditorShellModel = {
  readonly shortcutsOpen: boolean;
  readonly newProject: NewProjectDialogModel;
  /** Narrow More menu → About window (desktop) or pop-up (web, mobile). */
  readonly about: AboutLauncherModel;
};

export const initialEditorShellModel: EditorShellModel = {
  shortcutsOpen: false,
  newProject: initialNewProjectDialogModel,
  about: initialAboutLauncherModel,
};

export type EditorShellMsg =
  | { readonly type: "SetShortcutsOpen"; readonly open: boolean }
  | { readonly type: "ToggleShortcuts" }
  | { readonly type: "NewProject"; readonly msg: NewProjectDialogMsg }
  | { readonly type: "About"; readonly msg: AboutLauncherMsg };

export type EditorShellCmd =
  | { readonly scope: "newProject"; readonly cmd: NewProjectDialogCmd }
  | { readonly scope: "about"; readonly cmd: AboutLauncherCmd };

export function updateEditorShell(
  model: EditorShellModel,
  msg: EditorShellMsg,
): readonly [EditorShellModel, readonly EditorShellCmd[]] {
  switch (msg.type) {
    case "SetShortcutsOpen":
      return [{ ...model, shortcutsOpen: msg.open }, []];
    case "ToggleShortcuts":
      return [{ ...model, shortcutsOpen: model.shortcutsOpen === false }, []];
    case "NewProject": {
      const [newProject, cmds] = updateNewProjectDialog(
        model.newProject,
        msg.msg,
      );
      return [
        { ...model, newProject },
        cmds.map((cmd) => ({ scope: "newProject" as const, cmd })),
      ];
    }
    case "About": {
      const [about, cmds] = updateAboutLauncher(model.about, msg.msg);
      const closeShortcuts =
        msg.msg.type === "Open" && about.open && model.shortcutsOpen;
      return [
        {
          ...model,
          about,
          shortcutsOpen: closeShortcuts ? false : model.shortcutsOpen,
        },
        cmds.map((cmd) => ({ scope: "about" as const, cmd })),
      ];
    }
    default:
      return assertNever(msg);
  }
}

export const updateEditorShellFn: UpdateFn<
  EditorShellModel,
  EditorShellMsg,
  EditorShellCmd
> = updateEditorShell;
